import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  getVendorOrders,
  confirmOrder,
  shipOrder,
} from '../../redux/slices/vendorOrderSlice';
import { Package, Truck, CheckCircle, Clock, Search } from 'lucide-react';
import { toast } from 'sonner';

const VendorOrders = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const { orders, loading, error, actionLoading } = useSelector(
    (state) => state.vendorOrders
  );

  const [filters, setFilters] = useState({
    status: searchParams.get('status') || 'all',
    search: searchParams.get('search') || '',
  });

  const [shipModal, setShipModal] = useState(null);
  const [trackingData, setTrackingData] = useState({
    trackingNumber: '',
    trackingLink: '',
  });

  useEffect(() => {
    dispatch(getVendorOrders(filters));
  }, [dispatch, filters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    const params = new URLSearchParams();
    Object.keys(newFilters).forEach((k) => {
      if (newFilters[k] && newFilters[k] !== 'all') {
        params.set(k, newFilters[k]);
      }
    });
    setSearchParams(params);
  };

  const handleConfirm = async (orderId) => {
    try {
      await dispatch(confirmOrder(orderId)).unwrap();
      toast.success('Order confirmed successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to confirm order');
    }
  };

  const handleShip = async () => {
    if (!trackingData.trackingNumber) {
      toast.error('Please enter tracking number');
      return;
    }

    try {
      await dispatch(
        shipOrder({
          orderId: shipModal,
          ...trackingData,
        })
      ).unwrap();
      toast.success('Order marked as shipped');
      setShipModal(null);
      setTrackingData({ trackingNumber: '', trackingLink: '' });
    } catch (err) {
      toast.error(err.message || 'Failed to ship order');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-700',
      confirmed: 'bg-blue-100 text-blue-700',
      processing: 'bg-purple-100 text-purple-700',
      shipped: 'bg-green-100 text-green-700',
      delivered: 'bg-gray-100 text-gray-700',
      cancelled: 'bg-red-100 text-red-700',
    };

    return (
      <span className={`px-2 py-1 text-xs rounded ${badges[status] || 'bg-gray-100'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getItemStatus = (orderItems) => {
    if (!orderItems || orderItems.length === 0) return 'pending';
    return orderItems[0].vendorStatus || 'pending';
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Orders</h1>
        <p className="text-gray-600 mt-1">Manage your customer orders</p>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by order ID..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-bold mb-2">No Orders Found</h3>
          <p className="text-gray-600">You don't have any orders yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => {
            const itemStatus = getItemStatus(order.orderItems);

            return (
              <div key={order._id} className="bg-white rounded-lg shadow p-6">
                {/* Order Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-bold text-lg mb-1">
                      Order #{order._id.slice(-8).toUpperCase()}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {new Date(order.createdAt).toLocaleDateString('en-IN', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    {getStatusBadge(itemStatus)}
                    <p className="text-sm text-gray-600 mt-2">
                      Customer: {order.user?.name}
                    </p>
                  </div>
                </div>

                {/* Order Items */}
                <div className="border-t pt-4 mb-4">
                  {order.orderItems?.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 mb-3">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                      <div className="flex-1">
                        <h4 className="font-medium">{item.name}</h4>
                        <p className="text-sm text-gray-600">
                          {item.size && `Size: ${item.size}`}
                          {item.size && item.color && ' | '}
                          {item.color && `Color: ${item.color}`}
                        </p>
                        <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">₹{item.price * item.quantity}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="border-t pt-4 mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Items Total:</span>
                    <span className="font-semibold">₹{order.vendorTotal}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Commission:</span>
                    <span className="text-red-600">-₹{order.vendorCommission}</span>
                  </div>
                  <div className="flex justify-between font-bold">
                    <span>Your Earnings:</span>
                    <span className="text-green-600">₹{order.vendorEarnings}</span>
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="border-t pt-4 mb-4">
                  <h4 className="font-semibold mb-2 text-sm">Shipping Address:</h4>
                  <p className="text-sm text-gray-600">
                    {order.shippingAddress.address}, {order.shippingAddress.city},{' '}
                    {order.shippingAddress.state} - {order.shippingAddress.postalCode}
                    <br />
                    Mobile: {order.shippingAddress.mobile}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  {itemStatus === 'pending' && (
                    <button
                      onClick={() => handleConfirm(order._id)}
                      disabled={actionLoading}
                      className="px-4 py-2 bg-blue-600 text-white rounded flex items-center gap-2 hover:bg-blue-700 disabled:opacity-50"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Confirm Order
                    </button>
                  )}

                  {(itemStatus === 'confirmed' || itemStatus === 'processing') && (
                    <button
                      onClick={() => setShipModal(order._id)}
                      disabled={actionLoading}
                      className="px-4 py-2 bg-green-600 text-white rounded flex items-center gap-2 hover:bg-green-700 disabled:opacity-50"
                    >
                      <Truck className="w-4 h-4" />
                      Mark as Shipped
                    </button>
                  )}

                  {itemStatus === 'shipped' && (
                    <div className="px-4 py-2 bg-gray-100 rounded flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Awaiting Delivery Confirmation
                    </div>
                  )}

                  {itemStatus === 'delivered' && (
                    <div className="px-4 py-2 bg-green-100 text-green-700 rounded flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Delivered
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Ship Modal */}
      {shipModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Mark Order as Shipped</h3>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Tracking Number *
                </label>
                <input
                  type="text"
                  value={trackingData.trackingNumber}
                  onChange={(e) =>
                    setTrackingData({ ...trackingData, trackingNumber: e.target.value })
                  }
                  placeholder="Enter tracking number"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Tracking Link (Optional)
                </label>
                <input
                  type="url"
                  value={trackingData.trackingLink}
                  onChange={(e) =>
                    setTrackingData({ ...trackingData, trackingLink: e.target.value })
                  }
                  placeholder="https://tracking-url.com"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => {
                  setShipModal(null);
                  setTrackingData({ trackingNumber: '', trackingLink: '' });
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleShip}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {actionLoading ? 'Shipping...' : 'Confirm Shipment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorOrders;
